import { siteTexts } from "../../../../shared/i18n/siteTexts";
import type { ChallengeHistoryItem } from "../../../challenges/types/challengeAdmin";

type ProfileEventLocationProps = {
  item: ChallengeHistoryItem;
  onCopyError: (message: string) => void;
  onCopySuccess: (message: string) => void;
};

function buildLocationLabel(item: ChallengeHistoryItem) {
  const address = String(item.address || "").trim();
  const locality = [item.city, item.state]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" / ");

  return (
    [address, locality].filter(Boolean).join(" - ") ||
    siteTexts.challenges.publicDetail.labels.locationFallback
  );
}

function buildLocationQuery(item: ChallengeHistoryItem) {
  return [item.address, item.city, item.state]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(", ");
}

function buildMapUrl(item: ChallengeHistoryItem) {
  const query = buildLocationQuery(item);
  if (!query) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

export default function ProfileEventLocation({
  item,
  onCopyError,
  onCopySuccess,
}: ProfileEventLocationProps) {
  const locationText = siteTexts.profile.user.challenges;
  const label = buildLocationLabel(item);
  const query = buildLocationQuery(item);
  const mapUrl = buildMapUrl(item);
  const canInteract = Boolean(query);

  const handleCopy = async () => {
    if (!query) return;
    try {
      await navigator.clipboard.writeText(query);
      onCopySuccess(locationText.locationCopied);
    } catch (_error) {
      onCopyError(locationText.copyLocationError);
    }
  };

  return (
    <div className="user-event-meta user-event-meta--location">
      {canInteract ? (
        <a
          className="user-event-location__primary user-event-location__primary--interactive"
          href={mapUrl}
          target="_blank"
          rel="noreferrer"
          title={locationText.openMap}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            location_on
          </span>
          <span>{label}</span>
        </a>
      ) : (
        <div className="user-event-location__primary">
          <span className="material-symbols-outlined" aria-hidden="true">
            location_on
          </span>
          <span>{label}</span>
        </div>
      )}

      {canInteract ? (
        <div className="user-event-location__actions">
          <a
            className="user-event-location__action"
            href={mapUrl}
            target="_blank"
            rel="noreferrer"
            title={locationText.openMap}
            aria-label={locationText.openMap}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              map
            </span>
          </a>
          <button
            type="button"
            className="user-event-location__action"
            onClick={handleCopy}
            title={locationText.copyLocation}
            aria-label={locationText.copyLocation}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              content_copy
            </span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
